import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { LogOut, LayoutDashboard, FileImage, History, Users, ScrollText, Images, Settings, FileClock, Printer } from "lucide-react";

const Logo = () => (
  <div className="flex items-center gap-2.5" data-testid="brand-logo">
    <img src="/logo-icon.png" alt="Zuventus" className="h-9 w-auto rounded-lg object-contain" />
    <div className="leading-tight">
      <div className="font-display font-bold text-slate-900 text-sm">Zuventus</div>
      <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500">Master of ICU</div>
    </div>
  </div>
);

const NavItem = ({ to, icon: Icon, children, active, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className={
      "flex items-center gap-2 md:gap-3 px-3 py-2 md:py-2.5 rounded-lg text-sm font-medium whitespace-nowrap shrink-0 transition-colors " +
      (active
        ? "bg-brand-800 text-white"
        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900")
    }
  >
    <Icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
    <span>{children}</span>
  </Link>
);

export default function Shell({ children, role }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  const isActive = (p) => loc.pathname === p || (p !== "/admin" && p !== "/mr" && loc.pathname.startsWith(p));

  const adminNav = (
    <>
      <NavItem to="/admin" icon={LayoutDashboard} active={loc.pathname === "/admin"} testid="nav-admin-dashboard">Dashboard</NavItem>
      <NavItem to="/admin/requests" icon={Images} active={isActive("/admin/requests")} testid="nav-admin-requests">Certificate Requests</NavItem>
      <NavItem to="/admin/processed" icon={Printer} active={isActive("/admin/processed")} testid="nav-admin-processed">Certificates Processed</NavItem>
      <NavItem to="/admin/employees" icon={Users} active={isActive("/admin/employees")} testid="nav-admin-employees">Employees</NavItem>
      <NavItem to="/admin/audit" icon={ScrollText} active={isActive("/admin/audit")} testid="nav-admin-audit">Audit Log</NavItem>
      <NavItem to="/admin/settings" icon={Settings} active={isActive("/admin/settings")} testid="nav-admin-settings">Settings</NavItem>
    </>
  );

  const mrNav = (
    <>
      <NavItem to="/mr" icon={LayoutDashboard} active={loc.pathname === "/mr"} testid="nav-mr-dashboard">Dashboard</NavItem>
      <NavItem to="/mr/generate" icon={FileImage} active={isActive("/mr/generate")} testid="nav-mr-generate">Create Certificate</NavItem>
      <NavItem to="/mr/drafts" icon={FileClock} active={isActive("/mr/drafts")} testid="nav-mr-drafts">Drafts</NavItem>
      <NavItem to="/mr/history" icon={History} active={isActive("/mr/history")} testid="nav-mr-history">My Certificates</NavItem>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="w-full md:w-64 md:min-h-screen bg-white md:border-r border-slate-200 md:flex md:flex-col md:sticky md:top-0 md:h-screen" data-testid="app-sidebar">
        {/* Header row: logo + (mobile) user/logout */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 md:py-7 md:px-6 border-b border-slate-200 md:border-b-0">
          <Logo />
          <div className="md:hidden flex items-center gap-2">
            <div className="text-right leading-tight min-w-0">
              <div className="text-xs font-semibold text-slate-900 truncate max-w-[110px]" data-testid="user-name-mobile">{user?.name}</div>
              <div className="text-[10px] text-slate-500 truncate" data-testid="user-emp-code-mobile">{user?.emp_code}</div>
            </div>
            <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={handleLogout} data-testid="logout-button-mobile">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Desktop vertical nav */}
        <nav className="hidden md:flex flex-col gap-1 px-4 pb-6 flex-1">
          {role === "admin" ? adminNav : mrNav}
        </nav>

        {/* Mobile horizontal nav */}
        <nav className="md:hidden flex gap-2 overflow-x-auto px-3 py-2 no-scrollbar border-b border-slate-200">
          {role === "admin" ? adminNav : mrNav}
        </nav>

        {/* Desktop user footer */}
        <div className="hidden md:block border-t border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-brand-800 grid place-items-center text-white font-bold text-sm">
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate" data-testid="user-emp-code">{user?.emp_code}</div>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 fade-in" data-testid="app-main">
        <div className="px-4 py-6 md:px-10 md:py-10 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
